import {
  MAX_SESSIONS_FETCH_PAGES,
  SESSIONS_FETCH_PAGE_SIZE,
} from '@/constants';
import { mapSessionQueryParams } from '@/utils';

import { api } from './index';

import type {
  CoachAttendanceReportResponse,
  MemberChemistryBookingDay,
  Session,
  SessionDashboard,
  SessionRescheduleOptionsResponse,
  SessionUnavailabilityDiagnostics,
} from '@/types';
import type { SessionStatus } from '@/types/session';
import type { GetUserSessionsOptions } from '@/utils';

import { RFC_7231_TIMESTAMP } from '@/features/coachSchedule/calendarGrid';

export type CoachCancelSessionScope = 'single' | 'this_and_following';
export type CoachCancelSessionInitiator = 'coach' | 'member';

const actingRoleHeaders = (actingRole?: 'member' | 'coach') =>
  actingRole ? { 'X-Mento-Acting-Role': actingRole } : undefined;

export interface CoachCancelSessionScopeOption {
  count: number;
  scope: CoachCancelSessionScope;
  windowEnd?: string | null;
  windowStart?: string | null;
  window_end?: string | null;
  window_start?: string | null;
}

export interface CoachCancelSessionOptionsResponse {
  booking_source: string;
  proximity_minutes: number;
  recurring_id?: string | null;
  scopes: CoachCancelSessionScopeOption[];
  session_id: string;
  within_48_hours: boolean;
}

export interface CoachCancelSessionRequest {
  id: string;
  initiatedBy: CoachCancelSessionInitiator;
  opsNote?: string;
  scope: CoachCancelSessionScope;
  timezone?: string | null;
}

export interface CoachCancelSessionResponse {
  cancelled?: number;
  cancelledSessionsCount?: number;
  windowEnd?: string | null;
  windowStart?: string | null;
  cancelled_sessions_count?: number;
  window_end?: string | null;
  window_start?: string | null;
}

export type SessionRescheduleScope = 'single' | 'future_series';

export interface SessionRescheduleSlot {
  startTime: string;
  end: string;
  available: boolean;
  label?: string;
}

export interface CoachSessionRescheduleOptions {
  timezone?: string | null;
  slots: SessionRescheduleSlot[];
  sessionSeries: {
    futureSessionCount?: number;
    intervalWeeks?: number;
    startsOn?: string | null;
    endsOn?: string | null;
  } | null;
}

export interface RescheduleSessionArg {
  id: string;
  mutationId: string;
  scope: SessionRescheduleScope;
  startTime: string;
  viewerTimezone: string;
  overrideUnavailable: boolean;
}

export interface RescheduleSessionResponse {
  rescheduledCount: number;
  sessions: Session[];
  within48h: boolean;
  proximitySeconds: number;
}

export interface SessionRescheduleOptionsArg {
  date: string;
  id: string;
  scope: SessionRescheduleScope;
  viewerTimezone: string;
}

interface AdminSessionIdentity {
  coachId: string;
  id: string;
  memberId: string;
}

export interface AdminChemistryRescheduleOptions {
  days: MemberChemistryBookingDay[];
  timezone?: string | null;
}

// The shape `/sessions` is cast to. Every field is optional because the cast
// is unchecked: a proxy error page or a trimmed body would otherwise crash the
// page walk on `Object.entries(undefined)` instead of degrading to an empty
// list, which is what the previous `query:` form did.
interface SessionsPage {
  items?: Record<string, Session[]>;
  pages?: number;
  total?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Rails sends RFC 7231 HTTP-dates; ISO 8601 stays accepted. Both carry an
// explicit time and zone, so a date-only value still fails closed.
const isoTimestamp =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/u;
const isHistoryTimestamp = (value: string): boolean =>
  isoTimestamp.test(value) || RFC_7231_TIMESTAMP.test(value);

// History cannot let default normalization turn a malformed page into an empty
// success. Axios has already converted wire keys to camelCase at this boundary.
const validateHistorySessionsPage = (
  payload: unknown,
  seenIds: Set<string>,
  initialPage?: SessionsPage,
): string | null => {
  if (!isRecord(payload) || !isRecord(payload.items)) {
    return 'Invalid history sessions page: expected an object with items buckets';
  }
  const { items, pages, total } = payload;
  if (
    typeof pages !== 'number' ||
    !Number.isSafeInteger(pages) ||
    pages < 0 ||
    typeof total !== 'number' ||
    !Number.isSafeInteger(total) ||
    total < 0
  ) {
    return 'Invalid history sessions page: pages and total must be nonnegative integers';
  }
  if (
    initialPage &&
    (pages !== initialPage.pages || total !== initialPage.total)
  ) {
    return 'History sessions pagination metadata changed; retry the history read';
  }
  let rowCount = 0;
  for (const rows of Object.values(items)) {
    if (!Array.isArray(rows)) {
      return 'Invalid history sessions page: each items bucket must be an array';
    }
    rowCount += rows.length;
    for (const row of rows) {
      if (
        !isRecord(row) ||
        typeof row.id !== 'string' ||
        !row.id.trim() ||
        typeof row.userId !== 'string' ||
        !row.userId.trim() ||
        typeof row.coachId !== 'string' ||
        !row.coachId.trim() ||
        typeof row.status !== 'string' ||
        !row.status.trim() ||
        !isRecord(row.user) ||
        row.user.id !== row.userId ||
        !isRecord(row.coach) ||
        row.coach.id !== row.coachId
      ) {
        return 'Invalid history sessions page: session and participant identities must be present and consistent';
      }
      if (
        typeof row.startedAt !== 'string' ||
        !isHistoryTimestamp(row.startedAt) ||
        typeof row.endedAt !== 'string' ||
        !isHistoryTimestamp(row.endedAt) ||
        !Number.isFinite(Date.parse(row.startedAt)) ||
        !Number.isFinite(Date.parse(row.endedAt)) ||
        Date.parse(row.endedAt) <= Date.parse(row.startedAt)
      ) {
        return 'Invalid history sessions page: session timestamps must be valid and ordered';
      }
      if (seenIds.has(row.id)) {
        return 'History sessions pagination repeated a session; retry the history read';
      }
      seenIds.add(row.id);
    }
  }
  if (rowCount > total || (pages === 0 && total !== 0)) {
    return 'Invalid history sessions page: empty pagination metadata is inconsistent';
  }
  if (rowCount === 0 && total > 0) {
    return 'History sessions page is empty despite a positive total; retry the history read';
  }
  if (pages > MAX_SESSIONS_FETCH_PAGES) {
    return 'History sessions pagination exceeded request limit';
  }
  return null;
};

// `/sessions` returns rows already grouped into date buckets, so merging two
// pages means concatenating each bucket rather than replacing it. Both helpers
// copy instead of mutating: the RTK cache hands out the payload by reference.
const cloneSessionItems = (items: Record<string, Session[]> | undefined) => {
  const cloned: Record<string, Session[]> = {};
  Object.entries(items ?? {}).forEach(([bucket, sessions]) => {
    cloned[bucket] = [...sessions];
  });
  return cloned;
};

// Offset paging is not a stable snapshot: a session created, completed, or
// cancelled between two page requests shifts every row after it, so the same
// session can be served on both pages. SessionList keys rows by `session.id`,
// so an un-deduped merge renders the row twice under a duplicate React key.
const mergeSessionItems = (
  base: Record<string, Session[]>,
  incoming: Record<string, Session[]> | undefined,
) => {
  const merged = cloneSessionItems(base);
  const seen = new Set(
    Object.values(merged)
      .flat()
      .map((session) => session.id),
  );
  Object.entries(incoming ?? {}).forEach(([bucket, sessions]) => {
    const fresh = sessions.filter((session) => !seen.has(session.id));
    fresh.forEach((session) => seen.add(session.id));
    if (fresh.length) merged[bucket] = [...(merged[bucket] ?? []), ...fresh];
  });
  return merged;
};

export const sessionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    adminCancelSession: builder.mutation<
      CoachCancelSessionResponse,
      AdminSessionIdentity & {
        initiatedBy: CoachCancelSessionInitiator;
        opsNote?: string;
        scope: CoachCancelSessionScope;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({ coachId, id, initiatedBy, memberId, opsNote, scope }) => ({
        data: {
          cancellation_initiator: initiatedBy,
          coach_id: coachId,
          member_id: memberId,
          note_to_ops: opsNote,
          scope,
        },
        method: 'POST',
        url: `/admin/calendar/sessions/${id}/cancel`,
      }),
    }),
    adminChemistryCancelSession: builder.mutation<
      CoachCancelSessionResponse,
      AdminSessionIdentity & {
        confirmedWithin48Hours: boolean;
        reason: string;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({ coachId, confirmedWithin48Hours, id, memberId, reason }) => ({
        data: {
          coach_id: coachId,
          confirmed_within_48_hours: confirmedWithin48Hours,
          member_id: memberId,
          reason,
        },
        method: 'POST',
        url: `/admin/calendar/sessions/${id}/chemistry_cancel`,
      }),
    }),
    adminChemistryRescheduleSession: builder.mutation<
      Session,
      AdminSessionIdentity & {
        mutationId: string;
        startTime: string;
        viewerTimezone: string;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({
        coachId,
        id,
        memberId,
        mutationId,
        startTime,
        viewerTimezone,
      }) => ({
        data: {
          coach_id: coachId,
          member_id: memberId,
          mutation_id: mutationId,
          start_time: startTime,
          viewer_timezone: viewerTimezone,
        },
        method: 'POST',
        url: `/admin/calendar/sessions/${id}/chemistry_reschedule`,
      }),
    }),
    adminGetChemistryRescheduleOptions: builder.query<
      AdminChemistryRescheduleOptions,
      AdminSessionIdentity & { visibleWeeks: number; viewerTimezone: string }
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ coachId, id, memberId, viewerTimezone, visibleWeeks }) => ({
        method: 'GET',
        params: {
          coach_id: coachId,
          member_id: memberId,
          viewer_timezone: viewerTimezone,
          visible_weeks: visibleWeeks,
        },
        url: `/admin/calendar/sessions/${id}/chemistry_reschedule_options`,
      }),
    }),
    adminGetSessionCancelOptions: builder.query<
      CoachCancelSessionOptionsResponse,
      AdminSessionIdentity
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ coachId, id, memberId }) => ({
        method: 'GET',
        params: { coach_id: coachId, member_id: memberId },
        url: `/admin/calendar/sessions/${id}/cancel_options`,
      }),
    }),
    adminGetSessionRescheduleOptions: builder.query<
      CoachSessionRescheduleOptions,
      AdminSessionIdentity & SessionRescheduleOptionsArg
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ coachId, date, id, memberId, scope, viewerTimezone }) => ({
        method: 'GET',
        params: {
          coach_id: coachId,
          date,
          member_id: memberId,
          scope,
          viewer_timezone: viewerTimezone,
        },
        url: `/admin/calendar/sessions/${id}/reschedule_options`,
      }),
    }),
    adminRescheduleSession: builder.mutation<
      RescheduleSessionResponse,
      AdminSessionIdentity & RescheduleSessionArg
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({
        coachId,
        id,
        memberId,
        mutationId,
        overrideUnavailable,
        scope,
        startTime,
        viewerTimezone,
      }) => ({
        data: {
          coach_id: coachId,
          member_id: memberId,
          mutation_id: mutationId,
          override_unavailable: overrideUnavailable,
          scope,
          start_time: startTime,
          viewer_timezone: viewerTimezone,
        },
        method: 'POST',
        url: `/admin/calendar/sessions/${id}/reschedule`,
      }),
    }),
    cancelSession: builder.mutation<
      Session,
      { reason: string; sessionId: string }
    >({
      invalidatesTags: (_result, _error, { sessionId }) => [
        { id: sessionId, type: 'Session' },
        'Session',
      ],
      query: ({ reason, sessionId }) => ({
        data: { reason },
        method: 'POST',
        url: `/session_actions/${sessionId}/cancel`,
      }),
    }),
    coachCancelSession: builder.mutation<
      CoachCancelSessionResponse,
      CoachCancelSessionRequest
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({ id, initiatedBy, opsNote, scope, timezone }) => ({
        data: {
          cancellation_initiator: initiatedBy,
          note_to_ops: opsNote,
          scope,
          ...(timezone ? { timezone } : {}),
        },
        method: 'POST',
        url: `/sessions/${id}/cancel_by_coach`,
      }),
    }),
    coachCancelSessionOptions: builder.query<
      CoachCancelSessionOptionsResponse,
      string
    >({
      providesTags: (_result, _error, id) => [{ id, type: 'Session' }],
      query: (id) => ({
        method: 'GET',
        url: `/sessions/${id}/cancel_options`,
      }),
    }),
    extendSessionsForUser: builder.mutation<
      {
        created: number;
        skipped: number;
        errors: string[];
        booking_uids: string[];
        booked_until?: string;
      },
      { userId: string; extendByCount?: number; extendUntil?: string }
    >({
      invalidatesTags: ['Session'],
      query: ({ userId, extendByCount, extendUntil }) => ({
        data: {
          ...(extendByCount !== undefined
            ? { extend_by_count: extendByCount }
            : {}),
          ...(extendUntil ? { extend_until: extendUntil } : {}),
        },
        method: 'POST',
        url: `/users/${userId}/sessions/extend`,
      }),
    }),
    getCoachAttendanceReport: builder.query<
      CoachAttendanceReportResponse,
      {
        coachId: string;
        limit?: number;
        page?: number;
        minDate?: string;
        maxDate?: string;
      }
    >({
      providesTags: ['Session'],
      query: ({ coachId, limit = 10, page = 1, minDate, maxDate }) => {
        const args: Array<[string, string]> = [
          ['page', String(page)],
          ['limit', String(limit)],
        ];
        if (minDate) args.push(['min_date', minDate]);
        if (maxDate) args.push(['max_date', maxDate]);
        const qs = new URLSearchParams(args).toString();

        return {
          method: 'GET',
          url: `/coach_profiles/${coachId}/attendance_report?${qs}`,
        };
      },
    }),
    // Coach-specific session counts
    getCoachSessionCount: builder.query<
      {
        pending: number;
        done: number;
        active: number;
        rescheduled: number;
        cancelled: number;
      },
      { coachId: string }
    >({
      providesTags: ['Session'],
      query: ({ coachId }) => ({
        method: 'GET',
        url: `/coach_profiles/${coachId}/sessions/count`,
      }),
    }),
    // Per-coach sessions dashboard (backend aggregate)
    getCoachSessionsDashboard: builder.query<
      {
        upcoming: Record<string, Session>;
        noUpcoming: string[];
        noSessions: string[];
      },
      { coachId: string; minDate?: string; maxDate?: string }
    >({
      providesTags: ['Session'],
      query: ({ coachId, minDate, maxDate }) => {
        const args: Array<[string, string]> = [];
        if (minDate) args.push(['min_date', minDate]);
        if (maxDate) args.push(['max_date', maxDate]);
        const qs = new URLSearchParams(args).toString();
        return {
          method: 'GET',
          url: `/coach_profiles/${coachId}/sessions/dashboard${qs ? `?${qs}` : ''}`,
        };
      },
    }),
    // Coach summary with member stage counts
    getCoachSummary: builder.query<
      {
        memberStageCounts: Array<[string, number]>;
        totalPastMembers: number;
        chemistryCallMembers: string[];
        noFutureSessions: string[];
      },
      { coachId: string; minDate?: string; maxDate?: string }
    >({
      providesTags: ['Session'],
      query: ({ coachId, minDate, maxDate }) => {
        const args: Array<[string, string]> = [];
        if (minDate) args.push(['min_date', minDate]);
        if (maxDate) args.push(['max_date', maxDate]);
        const qs = new URLSearchParams(args).toString();
        return {
          method: 'GET',
          url: `/coach_profiles/${coachId}/summary${qs ? `?${qs}` : ''}`,
        };
      },
    }),
    getSession: builder.query<Session, string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Session' }],
      query: (id) => ({
        method: 'GET',
        url: `/sessions/${encodeURIComponent(id)}`,
      }),
    }),
    getScopedSession: builder.query<
      Session,
      { actingRole: 'member' | 'coach'; id: string }
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ actingRole, id }) => ({
        headers: actingRoleHeaders(actingRole),
        method: 'GET',
        url: `/sessions/${encodeURIComponent(id)}`,
      }),
    }),
    getSessionAfterCancellationFailure: builder.query<Session, string>({
      query: (id) => ({
        method: 'GET',
        url: `/sessions/${encodeURIComponent(id)}`,
      }),
    }),
    getCoachSessionRescheduleOptions: builder.query<
      CoachSessionRescheduleOptions,
      SessionRescheduleOptionsArg
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ date, id, scope, viewerTimezone }) => ({
        method: 'GET',
        params: { date, scope, viewer_timezone: viewerTimezone },
        url: `/sessions/${id}/reschedule_options`,
      }),
    }),
    getSessionCount: builder.query<Array<[string, number]>, string>({
      providesTags: ['Session'],
      query: (userId) => ({
        method: 'GET',
        url: `/sessions/count?user_id=${userId}`,
      }),
    }),
    getSessionRescheduleOptions: builder.query<
      SessionRescheduleOptionsResponse,
      { id: string; viewerTimezone: string }
    >({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Session' }],
      query: ({ id, viewerTimezone }) => ({
        method: 'GET',
        params: { viewer_timezone: viewerTimezone },
        url: `/sessions/${id}/reschedule_options`,
      }),
    }),
    getSessions: builder.query<
      { items: Record<string, Session[]>; pages: number; total: number },
      GetUserSessionsOptions
    >({
      providesTags: ['Session'],
      query: (params) => ({
        headers: actingRoleHeaders(params.actingRole),
        method: 'GET',
        url: `/sessions?${mapSessionQueryParams(params)}`,
      }),
    }),
    // MEN-4928: the Sessions timeline is a single long scroll with no
    // pagination control, and the same component tree serves a coach reading
    // their own schedule — a viewer who passes one server page of completed
    // sessions well inside a year. Presenting the first page as the whole
    // list would silently hide the older rows, so this endpoint walks every
    // page `/sessions` reports and returns the merged set.
    //
    // The returned `pages` counts what is still UNFETCHED plus the one page
    // delivered here: 1 when the walk is complete, >1 only when the
    // MAX_SESSIONS_FETCH_PAGES backstop bound it. Callers keep reading
    // `pages > 1` as "there is more you cannot see".
    getAllSessions: builder.query<
      { items: Record<string, Session[]>; pages: number; total: number },
      GetUserSessionsOptions & { historyRead?: true; cacheScope?: string }
    >({
      providesTags: ['Session'],
      queryFn: async (params, _api, _extraOptions, baseQuery) => {
        const fetchPage = (page: number) =>
          baseQuery({
            headers: actingRoleHeaders(params.actingRole),
            method: 'GET',
            url: `/sessions?${mapSessionQueryParams({
              ...params,
              limit: params.limit ?? SESSIONS_FETCH_PAGE_SIZE,
              page,
            })}`,
          });

        const historyIds = new Set<string>();
        const first = await fetchPage(1);
        if (first.error) return { error: first.error };
        if (params.historyRead) {
          const validationError = validateHistorySessionsPage(
            first.data,
            historyIds,
          );
          if (validationError) {
            return { error: { data: validationError, status: 'CUSTOM_ERROR' } };
          }
        }
        const firstPage = first.data as SessionsPage;

        const serverPages = Math.max(firstPage.pages ?? 1, 1);
        const pagesToFetch = Math.min(serverPages, MAX_SESSIONS_FETCH_PAGES);
        let items = cloneSessionItems(firstPage.items);
        let total = firstPage.total ?? 0;

        for (let page = 2; page <= pagesToFetch; page += 1) {
          // Sequential on purpose: a page is only meaningful against the
          // ordering the previous one established, and a real history is a
          // handful of requests at this page size.
          // eslint-disable-next-line no-await-in-loop -- page N+1 is only meaningful against the ordering page N established
          const next = await fetchPage(page);
          if (next.error) return { error: next.error };
          if (params.historyRead) {
            const validationError = validateHistorySessionsPage(
              next.data,
              historyIds,
              firstPage,
            );
            if (validationError) {
              return {
                error: { data: validationError, status: 'CUSTOM_ERROR' },
              };
            }
          }
          const nextPage = next.data as SessionsPage;
          items = mergeSessionItems(items, nextPage.items);
          total = nextPage.total ?? total;
        }

        if (params.historyRead && historyIds.size !== firstPage.total) {
          return {
            error: {
              data: 'History sessions pagination did not cover the reported total; retry the history read',
              status: 'CUSTOM_ERROR',
            },
          };
        }

        return {
          data: { items, pages: serverPages - pagesToFetch + 1, total },
        };
      },
    }),
    // New: Fetch sessions across all members for a given coach
    getSessionsByCoach: builder.query<
      { items: Record<string, Session[]>; pages: number; total: number },
      {
        coachId: string;
        page?: number;
        limit?: number;
        minDate?: string;
        maxDate?: string;
        order?: string;
        statuses?: Array<SessionStatus>;
        userId?: string;
        viewerId?: string;
        actingRole?: 'member' | 'coach';
      }
    >({
      providesTags: ['Session'],
      query: ({
        coachId,
        page = 1,
        limit,
        minDate,
        maxDate,
        order,
        statuses = [],
        userId,
        actingRole,
      }) => {
        const args: Array<[string, string]> = [];
        if (coachId) args.push(['coach_id', coachId]);
        // MEN-4160: filter a coach's sessions to a single member (List view).
        if (userId) args.push(['user_id', userId]);
        if (page) args.push(['page', page.toString()]);
        if (limit) args.push(['limit', String(limit)]);
        if (minDate) args.push(['min_date', minDate]);
        if (maxDate) args.push(['max_date', maxDate]);
        if (order) args.push(['order', order]);
        // MEN-5349: Backend SessionsController#status splits params[:status]
        // on commas; send one scalar rather than relying on Array#split.
        if (statuses.length > 0) args.push(['status', statuses.join(',')]);
        const search = new URLSearchParams(args).toString();
        return {
          headers: actingRoleHeaders(actingRole),
          method: 'GET',
          url: `/sessions?${search}`,
        };
      },
    }),
    getSessionsDashboard: builder.query<SessionDashboard, void>({
      providesTags: ['Session'],
      query: () => ({ method: 'GET', url: '/sessions/dashboard' }),
    }),
    getUnavailabilityDiagnostics: builder.query<
      SessionUnavailabilityDiagnostics,
      | {
          sessionId: string;
          slotStartsAt: string;
          slotEndsAt: string;
        }
      | {
          coachEmail: string;
          memberEmail?: string;
          slotStartsAt: string;
          slotEndsAt: string;
        }
    >({
      query: (args) => {
        const { slotStartsAt, slotEndsAt } = args;
        return {
          method: 'GET',
          params: {
            ...('sessionId' in args
              ? { session_id: args.sessionId }
              : {
                  coach_email: args.coachEmail,
                  ...(args.memberEmail
                    ? { member_email: args.memberEmail }
                    : {}),
                }),
            slot_ends_at: slotEndsAt,
            slot_starts_at: slotStartsAt,
          },
          url: '/sessions/unavailability_diagnostics',
        };
      },
    }),
    rescheduleSession: builder.mutation<
      Session,
      {
        confirmedWithin48Hours: boolean;
        id: string;
        mutationId: string;
        startsAt: string;
        viewerTimezone: string;
      }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({
        confirmedWithin48Hours,
        id,
        mutationId,
        startsAt,
        viewerTimezone,
      }) => ({
        data: {
          confirmed_within_48_hours: confirmedWithin48Hours,
          mutation_id: mutationId,
          starts_at: startsAt,
          viewer_timezone: viewerTimezone,
        },
        method: 'POST',
        url: `/sessions/${id}/reschedule`,
      }),
    }),
    updateSession: builder.mutation<
      Session,
      { data: Partial<Session>; id: string }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({ data, id }) => ({
        data,
        method: 'PATCH',
        url: `/sessions/${id}`,
      }),
    }),
    coachRescheduleSession: builder.mutation<
      RescheduleSessionResponse,
      RescheduleSessionArg
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Session' },
        'Session',
      ],
      query: ({
        id,
        mutationId,
        overrideUnavailable,
        scope,
        startTime,
        viewerTimezone,
      }) => ({
        data: {
          mutation_id: mutationId,
          override_unavailable: overrideUnavailable,
          scope,
          start_time: startTime,
          viewer_timezone: viewerTimezone,
        },
        method: 'POST',
        url: `/sessions/${id}/reschedule`,
      }),
    }),
  }),
});

export const {
  useAdminCancelSessionMutation,
  useAdminChemistryCancelSessionMutation,
  useAdminChemistryRescheduleSessionMutation,
  useAdminGetChemistryRescheduleOptionsQuery,
  useAdminGetSessionCancelOptionsQuery,
  useAdminGetSessionRescheduleOptionsQuery,
  useAdminRescheduleSessionMutation,
  useCoachCancelSessionMutation,
  useCoachCancelSessionOptionsQuery,
  useGetCoachAttendanceReportQuery,
  useGetCoachSessionCountQuery,
  useGetCoachSessionsDashboardQuery,
  useGetCoachSummaryQuery,
  useGetSessionCountQuery,
  useGetSessionQuery,
  useGetScopedSessionQuery,
  useLazyGetSessionAfterCancellationFailureQuery,
  useGetCoachSessionRescheduleOptionsQuery,
  useGetSessionRescheduleOptionsQuery,
  useGetAllSessionsQuery,
  useGetSessionsQuery,
  useLazyGetSessionsQuery,
  useGetSessionsDashboardQuery,
  useLazyGetUnavailabilityDiagnosticsQuery,
  useLazyGetSessionsByCoachQuery,
  useCancelSessionMutation,
  useCoachRescheduleSessionMutation,
  useUpdateSessionMutation,
  useRescheduleSessionMutation,
  useGetSessionsByCoachQuery,
  useExtendSessionsForUserMutation,
} = sessionApi;
